import { Injectable } from '@angular/core';
import { BehaviorSubject, fromEvent, Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { setCurrentResponsiveTarget } from '../../state/layout.actions';
import { AppState, MediaQueryConfig, ResponsiveTarget } from '@immosky/types';

@Injectable({
  providedIn: 'root',
})
export class ResponsiveService {
  private readonly config = new Map<ResponsiveTarget, MediaQueryConfig>([
    [
      ResponsiveTarget.XS,
      {
        minWidth: 0,
        maxWidth: 599.98,
      },
    ],
    [
      ResponsiveTarget.SM,
      {
        minWidth: 600,
        maxWidth: 904.98,
      },
    ],
    [
      ResponsiveTarget.MD,
      {
        minWidth: 905,
        maxWidth: 1239,
      },
    ],
    [
      ResponsiveTarget.LG,
      {
        minWidth: 1240,
        maxWidth: 1439.98,
      },
    ],
    [
      ResponsiveTarget.XL,
      {
        minWidth: 1440,
        maxWidth: Infinity,
      },
    ],
  ]);

  private currentTarget$ = new BehaviorSubject<ResponsiveTarget>(
    ResponsiveTarget.XS
  );
  private windowWidth = 0;

  get currentTarget(): Observable<ResponsiveTarget> {
    return this.currentTarget$.asObservable();
  }

  constructor(private store: Store<AppState>) {
    this.setupListeners();
  }

  private setupListeners() {
    fromEvent(window, 'resize').subscribe(() => {
      this.update();
    });

    requestAnimationFrame(() => {
      this.update();
    });
  }

  public update() {
    const previousWindowWidth = this.windowWidth;

    if (previousWindowWidth !== window.innerWidth) {
      this.windowWidth = window.innerWidth;

      const previousTarget = this.currentTarget$.value;
      const currentTarget = this.determineCurrentTarget();

      if (previousTarget !== currentTarget) {
        this.store.dispatch(
          setCurrentResponsiveTarget({
            target: currentTarget,
          })
        );
        this.currentTarget$.next(currentTarget);
      }
    }
  }

  private determineCurrentTarget(): ResponsiveTarget {
    let currentTarget = ResponsiveTarget.XS;
    this.config.forEach((value, key) => {
      if (this.isActive(value)) {
        currentTarget = key;
      }
    });
    return currentTarget;
  }

  target(fromTarget: ResponsiveTarget, toTarget?: ResponsiveTarget): boolean {
    const from = this.config.get(fromTarget);
    const to = this.config.get(toTarget || fromTarget);

    const minWidth = from?.minWidth || 0;
    const maxWidth = to?.maxWidth || from?.maxWidth || 0;

    return this.isActive({
      minWidth,
      maxWidth,
    });
  }

  private isActive(queryConfig: MediaQueryConfig): boolean {
    const minWidth = queryConfig.minWidth;
    const maxWidth = queryConfig.maxWidth;

    const minWidthSatisfied = this.windowWidth >= minWidth;
    const maxWidthSatisfied = this.windowWidth <= maxWidth;

    return minWidthSatisfied && maxWidthSatisfied;
  }
}
